import { getLocaleString } from '@assets/i18n/config';
import type { CurrencyCodes } from '@generated/graphql';
import { currencySymbol } from '@utils/currency';
import { formatLastRefreshed } from '@utils/formatLastRefreshed';
import {
  formatMoneyValue,
  isCurrencySymbolAfter,
} from '@utils/moneyFormatting';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import { DATE_KEY, MONEY_KEY } from './reconFlagDrawerOverviewConstants';

/** Calendar date at string start before trusting {@link dayjs} parse (ISO-style details). */
const HAS_ISO_DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

export const toSentenceCaseLabel = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  if (!spaced) {
    return key;
  }
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

export const readString = (v: unknown): string | undefined => {
  if (typeof v === 'string' && v.trim() !== '') {
    return v;
  }
  if (typeof v === 'number' && !Number.isNaN(v)) {
    return String(v);
  }
  return undefined;
};

const readIsoDateString = (v: unknown): string | undefined => {
  const s = readString(v);
  if (!s || !HAS_ISO_DATE_PREFIX.test(s) || !dayjs(s).isValid()) {
    return undefined;
  }
  return s;
};

export const formatDateValue = (raw: string): string => {
  const d = dayjs(raw);
  if (!d.isValid()) {
    return raw;
  }
  return d.format('MMM D, YYYY');
};

export const formatDateTimeValue = (raw: string): string => {
  const d = dayjs(raw);
  if (!d.isValid()) {
    return raw;
  }
  return d.format('MMM D, YYYY h:mm A');
};

/** Date+time in Eastern (America/New_York) with a short timezone token (e.g. EDT). */
export const formatDateTimeInEastern = (raw: string): string => {
  const d = dayjs(raw);
  if (!d.isValid()) {
    return raw;
  }
  return formatLastRefreshed(d.toDate());
};

const looksLikeMoneyField = (key: string): boolean => MONEY_KEY.test(key);

/** Only keys that declare cent denomination (e.g. `amountCents`, `price_cents`). */
const isCentsDenominatedField = (key: string): boolean => /cents$/i.test(key);

const formatMoneyishNumber = (
  key: string,
  value: number,
  currencyCode: string | undefined,
): string => {
  const locale = getLocaleString();
  const code =
    currencyCode && /^[A-Z]{3}$/i.test(currencyCode)
      ? currencyCode.toUpperCase()
      : 'USD';

  if (!looksLikeMoneyField(key)) {
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 8 }).format(
      value,
    );
  }

  const symbol = currencySymbol[code as CurrencyCodes] ?? '$';

  if (isCentsDenominatedField(key)) {
    return formatMoneyValue(value, symbol, 2, true, true);
  }

  const sign = value < 0 ? '-' : '';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));

  return isCurrencySymbolAfter(locale)
    ? `${sign}${formatted} ${symbol}`
    : `${sign}${symbol}${formatted}`;
};

export const formatPrimitiveDetail = (
  t: TFunction,
  key: string,
  value: unknown,
  currencyHint: string | undefined,
): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? t('yes', { ns: 'shared' }) : t('no', { ns: 'shared' });
  }
  if (typeof value === 'number') {
    return formatMoneyishNumber(key, value, currencyHint);
  }
  if (typeof value === 'string') {
    const iso = readIsoDateString(value);
    if (iso && DATE_KEY.test(key)) {
      return formatDateValue(iso);
    }
    if (iso) {
      return formatDateTimeValue(iso);
    }
    return value;
  }
  return JSON.stringify(value);
};
